import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import HomeRoute from "@/components/auth/HomeRoute";
import InactiveStateHandler from "@/components/subscription/InactiveStateHandler";
import ErrorBoundary from "@/components/ErrorBoundary";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
// LandingPage removed - now redirecting to /journals
// Legacy pages removed: DocumentPage, TopicPage, SessionPage
// Teacher pages removed: TeacherRoute, MyTopicsPage, TopicEditorPage, LessonEditorPage
import LoginPage from "./pages/LoginPage";
import SignUpPage from "./pages/SignUpPage";
import WelcomeVideoPage from "./pages/WelcomeVideoPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import PersonalizationPage from "./pages/PersonalizationPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import AccountSettings from "./pages/AccountSettings";

import ExplorePage from "./pages/ExplorePage";
import DiscoverPage from "./pages/DiscoverPage";
import CommunityTopicDetailPage from "./pages/CommunityTopicDetailPage";
import ChatPage from "./components/chat/ChatPage";
import NotFound from "./pages/NotFound";
import AgentPage from "./pages/AgentPage";
import SuccessPage from "./pages/SuccessPage";
import CancelPage from "./pages/CancelPage";
import { Navigate } from "react-router-dom";
import SpacesPage from "./pages/SpacesPage";
import SpaceDetailPage from "./pages/SpaceDetailPage";
import CommunityJournalPage from "./pages/CommunityJournalPage";
import Landing from "./pages/Landing";
import CreatorPortal from "./pages/CreatorPortal";
import JournalGeneratorPage from "./pages/JournalGeneratorPage";
import UserManagementPage from "./pages/UserManagementPage";
import AnnouncementsPage from "./pages/admin/AnnouncementsPage";

// Create QueryClient with memoization to prevent HMR issues
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/success" element={<SuccessPage />} />
              <Route path="/cancel" element={<CancelPage />} />
              <Route path="/welcome-video" element={<ProtectedRoute><WelcomeVideoPage /></ProtectedRoute>} />

              {/* Home route - Landing for non-auth, SpacesPage for auth users */}
              <Route path="/" element={<HomeRoute />} />
              {/* Community page - Public access, copying journals requires auth */}
              <Route path="/community" element={<ExplorePage />} />
              <Route path="/community/:id" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <CommunityTopicDetailPage />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              {/* Community Journal View - Anyone can view, copy requires auth */}
              <Route path="/community/journal/:id" element={
                <CommunityJournalPage />
              } />
              <Route path="/discover" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <DiscoverPage />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              {/* /topic/:id route removed - topics functionality migrated to journals */}
              <Route path="/chat/:id" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <ChatPage />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              <Route path="/agent" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <AgentPage />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              <Route path="/journals" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <SpacesPage />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              <Route path="/journals/:journalId" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <SpaceDetailPage />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              {/* Onboarding removed (legacy) */}
              <Route path="/personalization" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <PersonalizationPage />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              <Route path="/account" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <AccountSettings />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />

              {/* Admin / Creator Portal - Role check handled in component */}
              <Route path="/admin" element={
                <ProtectedRoute>
                  <CreatorPortal />
                </ProtectedRoute>
              } />
              <Route path="/admin/generator" element={
                <ProtectedRoute>
                  <JournalGeneratorPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <ProtectedRoute>
                  <UserManagementPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/announcements" element={
                <ProtectedRoute>
                  <AnnouncementsPage />
                </ProtectedRoute>
              } />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </AuthProvider>
          <CookieConsentBanner />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
